// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  Permission,
  PermissionAction,
  PermissionActionBit,
  PermissionValue,
  isValidPermissionAction,
} from '@/authorization/types';

/**
 * Frontend implementation of PolicyManifest matching the backend Go implementation.
 * This class handles parsing and evaluating permission policies for resource access control.
 *
 * Policy Format:
 * Policies are represented as JSON arrays of strings in the format:
 * "resource.subresource:[action1,action2] = true/false"
 *
 * Examples:
 * ["time.rate:[read,update] = true", "asset:[*] = false"]
 */
export class PolicyManifest {
  private entries = new Map<string, PermissionValue>();

  private static readonly actionToBitMap: Record<
    PermissionAction,
    PermissionActionBit | null
  > = {
    create: PermissionActionBit.CREATE,
    read: PermissionActionBit.READ,
    update: PermissionActionBit.UPDATE,
    delete: PermissionActionBit.DELETE,
    '*': null,
  };

  /**
   * Creates a new PolicyManifest from an array of policy strings
   */
  constructor(policies?: string[] | null) {
    if (policies) {
      this.parseFromArray(policies);
    }
  }

  /**
   * Creates a PolicyManifest from a JSON policy array string
   */
  static fromJSON(policyJSON: string): PolicyManifest {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const policies = JSON.parse(policyJSON);
    if (!Array.isArray(policies)) {
      throw new Error('Policy JSON must be an array of strings');
    }
    return new PolicyManifest(policies as string[]);
  }

  /**
   * Parse policies from an array of policy strings
   */
  private parseFromArray(policies: string[]): void {
    for (const policy of policies) {
      this.parsePolicy(policy);
    }
  }

  /**
   * Parse a single policy string
   * Format: "resource:[actions] = permission"
   */
  private parsePolicy(policyStr: string): void {
    // Parse the entry: "resource:[actions] = permission"
    // Handle various spacing around the equals sign
    const equalIndex = policyStr.indexOf('=');
    if (equalIndex === -1) {
      throw new Error(`Invalid permission entry format: ${policyStr}`);
    }

    const leftPart = policyStr.substring(0, equalIndex).trim();
    const permissionValue = policyStr.substring(equalIndex + 1).trim();

    let allowed: boolean;
    if (permissionValue === 'true') {
      allowed = true;
    } else if (permissionValue === 'false') {
      allowed = false;
    } else {
      throw new Error(`Invalid permission value: ${permissionValue}`);
    }

    // Parse resource and actions: "resource:[actions]"
    const colonIndex = leftPart.indexOf(':');
    if (colonIndex === -1) {
      throw new Error(`Invalid entry format, missing colon: ${policyStr}`);
    }

    const resourceName = leftPart.substring(0, colonIndex).trim();
    const actionsPart = leftPart.substring(colonIndex + 1).trim();

    // Parse actions: "[action1,action2,...]"
    if (!actionsPart.startsWith('[') || !actionsPart.endsWith(']')) {
      throw new Error(`Invalid actions format: ${actionsPart}`);
    }

    const actionsStr = actionsPart.substring(1, actionsPart.length - 1).trim();
    let actionBits = 0;

    if (actionsStr === '*') {
      // Expand * to all actions
      actionBits =
        PermissionActionBit.CREATE |
        PermissionActionBit.READ |
        PermissionActionBit.UPDATE |
        PermissionActionBit.DELETE;
    } else if (actionsStr.length > 0) {
      const actions = actionsStr.split(',');
      for (const action of actions) {
        const trimmedAction = action.trim();
        if (!trimmedAction) continue; // Skip empty actions

        if (!isValidPermissionAction(trimmedAction)) {
          throw new Error(`Invalid action: ${trimmedAction}`);
        }

        const actionBit = PolicyManifest.actionToBitMap[trimmedAction];
        if (actionBit !== null) {
          actionBits |= actionBit;
        }
      }
    }

    // Get existing entry or create new one
    const existingValue = this.entries.get(resourceName) || {
      actions: 0,
      actionsAllowed: 0,
    };

    // Set the action bits (mark that these actions were configured)
    existingValue.actions |= actionBits;

    // Set or clear the allowed bits based on the permission value
    if (allowed) {
      existingValue.actionsAllowed |= actionBits;
    } else {
      // Clear the allowed bits for these actions using bitwise AND with NOT
      existingValue.actionsAllowed &= ~actionBits;
    }

    this.entries.set(resourceName, existingValue);
  }

  /**
   * Check if a permission is allowed for the given action on a resource
   */
  private hasPermissionValue(
    permissionValue: PermissionValue,
    action: PermissionAction
  ): Permission {
    const actionBit = PolicyManifest.actionToBitMap[action];
    if (actionBit === null) {
      return Permission.PermissionNotFound;
    }

    // Check if this action was configured
    if (permissionValue.actions & actionBit) {
      // Check if it's allowed
      if (permissionValue.actionsAllowed & actionBit) {
        return Permission.PermissionExplicitTrue;
      }
      return Permission.PermissionExplicitFalse;
    }

    return Permission.PermissionNotFound;
  }

  /**
   * Check if a permission is granted for a resource and action.
   * This implements the same logic as the backend Go implementation:
   * 1. Check for explicit grants (exact path match)
   * 2. Check for implicit grants (parent path inheritance)
   * 3. Check for global wildcard permission "*"
   */
  hasPermission(resourceName: string, action: PermissionAction): Permission {
    // Check the explicit grants (path matches exactly)
    const permissionValue = this.entries.get(resourceName);
    if (permissionValue) {
      const grant = this.hasPermissionValue(permissionValue, action);
      if (grant !== Permission.PermissionNotFound) {
        return grant;
      }
      // If it wasn't found, continue to implicit grants
    }

    // Now check for implicit grants (path is a child of a configured parent path)
    const paths = resourceName.split('.');
    // Remove the last part and check each parent
    for (let i = paths.length - 1; i > 0; i--) {
      const parentPath = paths.slice(0, i).join('.');
      const parentValue = this.entries.get(parentPath);
      if (parentValue) {
        const grant = this.hasPermissionValue(parentValue, action);
        if (grant !== Permission.PermissionNotFound) {
          // Transform explicit to implicit since this isn't the exact path
          if (grant === Permission.PermissionExplicitTrue) {
            return Permission.PermissionImplicitTrue;
          }
          return Permission.PermissionImplicitFalse;
        }
      }
    }

    // Finally, check for global wildcard permission "*"
    const globalValue = this.entries.get('*');
    if (globalValue) {
      const grant = this.hasPermissionValue(globalValue, action);
      if (grant !== Permission.PermissionNotFound) {
        // Global wildcard permissions are always implicit since they don't match exact resource
        if (grant === Permission.PermissionExplicitTrue) {
          return Permission.PermissionImplicitTrue;
        }
        return Permission.PermissionImplicitFalse;
      }
    }

    return Permission.PermissionNotFound;
  }

  /**
   * Serialize the policy manifest back to the JSON array format
   */
  toJSON(): string[] {
    const entries: string[] = [];

    for (const [resourceName, permissionValue] of this.entries.entries()) {
      // Build separate entries for allowed and denied actions
      const allowedActions: string[] = [];
      const deniedActions: string[] = [];

      if (permissionValue.actions & PermissionActionBit.CREATE) {
        if (permissionValue.actionsAllowed & PermissionActionBit.CREATE) {
          allowedActions.push('create');
        } else {
          deniedActions.push('create');
        }
      }
      if (permissionValue.actions & PermissionActionBit.READ) {
        if (permissionValue.actionsAllowed & PermissionActionBit.READ) {
          allowedActions.push('read');
        } else {
          deniedActions.push('read');
        }
      }
      if (permissionValue.actions & PermissionActionBit.UPDATE) {
        if (permissionValue.actionsAllowed & PermissionActionBit.UPDATE) {
          allowedActions.push('update');
        } else {
          deniedActions.push('update');
        }
      }
      if (permissionValue.actions & PermissionActionBit.DELETE) {
        if (permissionValue.actionsAllowed & PermissionActionBit.DELETE) {
          allowedActions.push('delete');
        } else {
          deniedActions.push('delete');
        }
      }

      // Create entry for allowed actions if any
      if (allowedActions.length > 0) {
        let actionsStr: string;
        const allActions =
          PermissionActionBit.CREATE |
          PermissionActionBit.READ |
          PermissionActionBit.UPDATE |
          PermissionActionBit.DELETE;
        if (
          permissionValue.actionsAllowed === allActions &&
          permissionValue.actions === allActions
        ) {
          actionsStr = '*';
        } else {
          actionsStr = allowedActions.join(',');
        }
        const entry = `${resourceName}:[${actionsStr}] = true`;
        entries.push(entry);
      }

      // Create entry for denied actions if any
      if (deniedActions.length > 0) {
        const actionsStr = deniedActions.join(',');
        const entry = `${resourceName}:[${actionsStr}] = false`;
        entries.push(entry);
      }
    }

    return entries;
  }
}
